import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
    };
  }
}

interface TurnstileProps {
  siteKey?: string;
  onToken: (token?: string) => void;
}

export function Turnstile({ siteKey, onToken }: TurnstileProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | undefined>();

  useEffect(() => {
    if (!siteKey || !ref.current) return;

    const render = () => {
      if (!ref.current || !window.turnstile || widgetId.current) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        theme: 'dark',
        size: 'compact',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(undefined),
        'error-callback': () => onToken(undefined)
      });
    };

    if (window.turnstile) {
      render();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.appendChild(script);

    return () => {
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
    };
  }, [siteKey, onToken]);

  if (!siteKey) return <p className="microcopy">Turnstile site key missing. Configure VITE_TURNSTILE_SITE_KEY.</p>;
  return <div className="turnstile" ref={ref} aria-label="Cloudflare Turnstile human check" />;
}
